<script setup lang="ts">
import type { AvatarImageProps } from 'reka-ui';

import { isProcessedImageUrl } from '@/utils/imageUrl';
import { NuxtImg } from '#components';
import { AvatarImage } from 'reka-ui';

interface Props {
	src?: AvatarImageProps['src'];
	alt?: string;
	width?: string | number;
	height?: string | number;
	format?: string;
	loading?: 'lazy' | 'eager';
}

const props = withDefaults(defineProps<Props>(), {
	src: '',
	alt: '',
	width: '128',
	height: '128',
	format: 'webp',
	loading: 'lazy'
});

const isAlreadyProcessed = computed(() => isProcessedImageUrl(props.src));

// Use dynamic component to avoid v-if/v-else inside as-child slot
// This prevents "Runtime directive used on component with non-element root node" warning
const imageComponent = computed(() =>
	isAlreadyProcessed.value ? 'img' : NuxtImg
);

// Props for the image component - only include format for NuxtImg
const imageProps = computed(() => {
	const baseProps = {
		src: props.src,
		alt: props.alt,
		width: props.width,
		height: props.height,
		loading: props.loading,
		class: 'size-full object-cover'
	};

	// Only add format for NuxtImg (not native img)
	if (!isAlreadyProcessed.value) {
		return { ...baseProps, format: props.format };
	}

	return baseProps;
});
</script>

<template>
	<AvatarImage :src="props.src" as-child>
		<component :is="imageComponent" v-bind="imageProps" />
	</AvatarImage>
</template>
